/*
 * What the page in the active tab hands the tab bar: the tab's name and
 * avatar, the page's own panel toggles, its actions menu, the details popover
 * the active tab opens, and where Back goes without in-app history.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type FC,
  type ReactElement,
  type ReactNode
} from "react";

import { useAppTabsStore, type TabMeta } from "./appTabsStore";

export type ActiveTabRegistration = {
  tabId: string;
  meta?: TabMeta;
  /** The page's own panel toggle, first in the bar. */
  left?: ReactNode;
  /** Page actions beside the agent button. */
  actions?: ReactNode;
  /** The page's other panel toggle, last in the bar (the left one's mirror). */
  right?: ReactNode;
  /** Wraps the active tab so clicking it opens the page's details popover. */
  renderDetails?: (trigger: ReactElement) => ReactNode;
  fallbackBackTo?: string | null;
};

type Setter = (reg: ActiveTabRegistration | null) => void;

const RegistrationContext = createContext<ActiveTabRegistration | null>(null);
const SetterContext = createContext<Setter | null>(null);

export const ActiveTabProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const [reg, setReg] = useState<ActiveTabRegistration | null>(null);
  return (
    <SetterContext.Provider value={setReg}>
      <RegistrationContext.Provider value={reg}>
        {children}
      </RegistrationContext.Provider>
    </SetterContext.Provider>
  );
};

export const useActiveTabRegistration = (): ActiveTabRegistration | null =>
  useContext(RegistrationContext);

/**
 * Register the page's tab controls while mounted, and keep the tab's name in
 * the store. Pass a memoized registration; a new identity re-registers.
 */
export function useRegisterActiveTab(reg: ActiveTabRegistration | null): void {
  const setReg = useContext(SetterContext);
  const setMeta = useAppTabsStore((s) => s.setMeta);

  useEffect(() => {
    if (!setReg || !reg) return undefined;
    setReg(reg);
    return () => setReg(null);
  }, [reg, setReg]);

  const tabId = reg?.tabId;
  const meta = reg?.meta;
  useEffect(() => {
    if (tabId && meta) setMeta(tabId, meta);
  }, [tabId, meta, setMeta]);
}
